import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ShoppingBag, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface WorkspaceOption {
  id: string;
  name: string;
}

/**
 * Landing page for Shopify-initiated managed install.
 * Shopify hits /shopify-oauth-callback?shop=...&hmac=... (no code/state),
 * the edge function validates HMAC and 302s the user here so we can
 * authenticate them, pick a workspace, and start our own OAuth.
 */
export default function ShopifyInstall() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const shop = params.get("shop") || "";
  const host = params.get("host") || "";

  const [checkingAuth, setCheckingAuth] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>("");
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check session
  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      const uid = data.session?.user?.id ?? null;
      setUserId(uid);
      setCheckingAuth(false);

      if (!uid) {
        const next = `/integrations/shopify/install?${params.toString()}`;
        navigate(`/auth?next=${encodeURIComponent(next)}`, { replace: true });
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate, params]);

  // Validate shop param
  useEffect(() => {
    if (!shop || !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(shop)) {
      setError("Domínio da loja Shopify inválido ou ausente.");
    }
  }, [shop]);

  // Load workspaces
  useEffect(() => {
    if (!userId) return;
    setLoadingWorkspaces(true);
    (async () => {
      const { data, error: err } = await supabase
        .from("workspace_members")
        .select("workspace_id, workspaces!workspace_members_workspace_id_fkey(name)")
        .eq("user_id", userId);
      setLoadingWorkspaces(false);
      if (err) {
        setError("Não foi possível carregar seus workspaces.");
        return;
      }
      const list: WorkspaceOption[] = (data || [])
        .map((r: any) => ({
          id: r.workspace_id as string,
          name: (r.workspaces as any)?.name || "Workspace",
        }))
        .filter((w) => !!w.id);
      setWorkspaces(list);
      if (list.length === 1) setSelectedWorkspace(list[0].id);
      if (list.length === 0) {
        setError("Você ainda não possui um workspace. Crie ou entre em um workspace antes de conectar a Shopify.");
      }
    })();
  }, [userId]);

  const handleConnect = async () => {
    if (!selectedWorkspace || !shop) return;
    setStarting(true);
    try {
      const { data, error: err } = await supabase.functions.invoke("shopify-oauth", {
        body: {
          workspaceId: selectedWorkspace,
          shop,
          returnOrigin: window.location.origin,
        },
      });
      if (err || !(data as any)?.authUrl) {
        throw new Error(err?.message || (data as any)?.error || "Falha ao iniciar OAuth");
      }
      // Top-level redirect (Shopify requires this — no iframes for OAuth)
      window.location.href = (data as any).authUrl as string;
    } catch (e: any) {
      setStarting(false);
      toast({
        title: "Erro ao iniciar conexão",
        description: e?.message || "Tente novamente em instantes.",
        variant: "destructive",
      });
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <ShoppingBag className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Conectar Shopify</CardTitle>
              <CardDescription className="break-all">{shop || "—"}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>{error}</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                Escolha o workspace do FabChat para conectar à sua loja Shopify.
                Você será redirecionado à Shopify para autorizar a instalação.
              </p>

              {loadingWorkspaces ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando workspaces…
                </div>
              ) : workspaces.length > 1 ? (
                <Select value={selectedWorkspace} onValueChange={setSelectedWorkspace}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um workspace" />
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : workspaces.length === 1 ? (
                <div className="text-sm">
                  Workspace: <span className="font-medium">{workspaces[0].name}</span>
                </div>
              ) : null}

              <Button
                className="w-full"
                disabled={!selectedWorkspace || starting || !shop}
                onClick={handleConnect}
              >
                {starting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Redirecionando…
                  </>
                ) : (
                  "Continuar para Shopify"
                )}
              </Button>

              <Button
                variant="ghost"
                className="w-full"
                onClick={() => navigate("/integrations")}
                disabled={starting}
              >
                Cancelar
              </Button>
            </>
          )}

          {host ? <input type="hidden" value={host} readOnly /> : null}
        </CardContent>
      </Card>
    </div>
  );
}
